import ContactInterface from "@/interfaces/contact.interface";
import axios from "@/libs/axios";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { isEqual } from "lodash";
import { reactive, ref, toRefs } from "@vue/composition-api";
import { useQuery } from "@tanstack/vue-query";

interface RootState {
    contacts: ContactInterface[];
    loading: boolean;
    error: boolean;
}

const state: RootState = reactive({
    contacts: [],
    loading: false,
    error: false,
});
const metadataFields = ref([]);

export default function useContacts() {
    const fetchContacts = (queryParams?: AxiosRequestConfig): void => {
        const { data, isLoading, isError } = useQuery(["contacts"], () => axios.get("/contacts", queryParams), {
            select: (response: { data: ContactInterface[] }) => response.data,
            refetchOnWindowFocus: false,
            staleTime: 5000,
            placeholderData: { data: [] } as any,
        });

        // @ts-ignore
        state.loading = isLoading;
        // @ts-ignore
        state.contacts = data;
        // @ts-ignore
        state.error = isError;
    };

    const fetchContact = (contactId: number, queryParams?: AxiosRequestConfig): Promise<ContactInterface> => {
        return axios
            .get(`/contacts/${contactId}`, queryParams)
            .then((response: AxiosResponse<ContactInterface>) => response.data);
    };

    const addContact = async (contactId: number): Promise<ContactInterface> => {
        const contact = await fetchContact(contactId);

        state.contacts.push(contact);

        return contact;
    };

    const updateContact = (
        contactData: ContactInterface,
        originalData?: ContactInterface,
        index?: number
    ): Promise<{ originalData: ContactInterface; updatedData: ContactInterface | undefined }> => {
        state.loading = true;

        if (contactData.metadata) {
            // Filter out empty metadata to avoid empty values in the database.
            contactData.metadata = contactData.metadata.filter((field) => field.value || field.id);
        }

        // @ts-ignore
        return _checkDuplicates(contactData, originalData)
            .then(async () => {
                if (!index && !contactData.id) {
                    throw new Error(
                        "Cannot determine what contact you like to update. Please pass 'id' property or set index parameter."
                    );
                }

                const contactsListIndex =
                    index ??
                    state.contacts.findIndex((listContact: { id: number }) => listContact.id === contactData.id);

                if (contactsListIndex !== -1) {
                    const updatedData = { ...state.contacts[contactsListIndex], ...contactData };
                    if (isEqual(updatedData, state.contacts[contactsListIndex])) {
                        return {
                            originalData: state.contacts[contactsListIndex],
                            updatedData: undefined,
                        };
                    }

                    const splice = state.contacts.splice(contactsListIndex, 1, updatedData);

                    await axios.patch(`/contacts/${updatedData.id}`, updatedData);

                    return {
                        originalData: splice[0],
                        updatedData,
                    };
                }
            })
            .finally(() => (state.loading = false));
    };

    const _checkDuplicates = async (
        contact: ContactInterface,
        originalContact: ContactInterface
    ): Promise<boolean | void> => {
        return new Promise(async (resolve, reject) => {
            if (!originalContact) {
                return resolve();
            }

            // Do we actually HAVE a phone and email, if not, we can't check duplicates.
            if (!contact.phone && !contact.email) {
                return resolve();
            }

            const searchQuery = [];

            // Existing contact data has no updated phone or email? Skip duplicate check.
            if (contact.email === originalContact.email && contact.phone === originalContact.phone) {
                return resolve();
            }

            if (contact.phone?.length > 0 && contact.phone !== originalContact.phone) {
                searchQuery.push({ phone: contact.phone });
            }

            if (contact.email?.length > 0 && contact.email !== originalContact.email) {
                searchQuery.push({ email: contact.email });
            }

            if (searchQuery.length === 0) {
                return resolve();
            }

            const duplicateContacts = await axios.get("/contacts", {
                params: {
                    s: {
                        $or: searchQuery,
                    },
                },
            });

            if (duplicateContacts.data.length > 0) {
                return reject("contact-already-exists");
            }

            return resolve();
        });
    };

    const fetchMetadataFields = () => {
        axios
            .get("/contacts/metadata", {
                params: {
                    distinct: true,
                    fields: "name",
                },
            })
            .then((response) => (metadataFields.value = response.data));
    };

    return {
        fetchContacts,
        fetchContact,
        addContact,
        updateContact,
        ...toRefs(state),
        metadataFields,
        fetchMetadataFields,
    };
}
